import { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { Extension } from "@tiptap/react";
import {
  type BlockSelectionRange,
  findInlineAtomRangesInRange,
} from "../../ranges/opaque-atom-range";

const CLARIFICATION_BLOCK_TYPES = [
  "paragraph",
  "heading",
  "bulletList",
  "orderedList",
  "codeBlock",
  "blockquote",
  "image",
  "atomicField",
] as const;

const BLOCK_ID_ATTR = "blockId";
const CLARIFICATION_SELECTION_TEXT_CLASS = "df-clarification-selection-text";
const CLARIFICATION_SELECTION_ATOM_CLASS = "df-clarification-selection-atom";

type ClarificationBlockType = (typeof CLARIFICATION_BLOCK_TYPES)[number];

function isClarificationBlockType(typeName: string): typeName is ClarificationBlockType {
  return CLARIFICATION_BLOCK_TYPES.includes(typeName as ClarificationBlockType);
}

function normalizeFieldId(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function normalizeBlockId(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function escapeSelectorValue(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/["\\]/g, "\\$&");
}

function isMarkupNode(typeName: string): boolean {
  return typeName === "codeBlock" || typeName === "image";
}

function isAtomicNode(typeName: string): boolean {
  return typeName === "atomicField";
}

function isListNode(typeName: string): boolean {
  return typeName === "bulletList" || typeName === "orderedList";
}

function extractPreviewText(value: string): string {
  const compact = value.replace(/\s+/g, " ").trim();
  if (compact.length <= 120) return compact;
  return `${compact.slice(0, 117)}...`;
}

function compactMatchText(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

function hasMeaningfulBlockContent(node: PMNode): boolean {
  return (node.textContent ?? "").trim().length > 0;
}

function extractNodePreviewText(node: PMNode): string {
  const fromTextContent = extractPreviewText(node.textContent ?? "");
  if (fromTextContent.length > 0) return fromTextContent;

  const attrs = (node.attrs ?? {}) as Record<string, unknown>;
  const label = typeof attrs.label === "string" ? attrs.label.trim() : "";
  const fieldId = typeof attrs.fieldId === "string" ? attrs.fieldId.trim() : "";

  if (node.type.name === "atomicField") {
    const value = typeof attrs.value === "string" ? attrs.value.trim() : "";
    return extractPreviewText(value || label || fieldId || "Atomic field");
  }

  if (node.type.name === "image") {
    const alt = typeof attrs.alt === "string" ? attrs.alt.trim() : "";
    const title = typeof attrs.title === "string" ? attrs.title.trim() : "";
    return extractPreviewText(alt || title || "Image");
  }

  return extractPreviewText(label || fieldId || node.type.name);
}

function createIconElement(type: "messageSquarePlus" | "messageSquare"): SVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "14");
  svg.setAttribute("height", "14");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");

  const outline = document.createElementNS(ns, "path");
  outline.setAttribute("d", "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z");
  svg.append(outline);

  if (type === "messageSquarePlus") {
    const vertical = document.createElementNS(ns, "line");
    vertical.setAttribute("x1", "12");
    vertical.setAttribute("x2", "12");
    vertical.setAttribute("y1", "8");
    vertical.setAttribute("y2", "14");
    const horizontal = document.createElementNS(ns, "line");
    horizontal.setAttribute("x1", "9");
    horizontal.setAttribute("x2", "15");
    horizontal.setAttribute("y1", "11");
    horizontal.setAttribute("y2", "11");
    svg.append(vertical, horizontal);
  } else {
    const middle = document.createElementNS(ns, "path");
    middle.setAttribute("d", "M8.5 11h7");
    svg.append(middle);
  }

  return svg;
}

function findSectionFieldId(
  doc: Parameters<typeof DecorationSet.create>[0],
  pos: number
): string | null {
  const safePos = Math.max(0, Math.min(pos, doc.content.size));
  const $pos = doc.resolve(safePos);
  for (let depth = $pos.depth; depth >= 0; depth -= 1) {
    const node = $pos.node(depth);
    const fieldId = normalizeFieldId(node.attrs?.fieldId);
    if (fieldId) {
      return fieldId;
    }
  }
  return null;
}

function resolveBlockFieldId(
  doc: Parameters<typeof DecorationSet.create>[0],
  node: PMNode,
  pos: number
): string {
  return findSectionFieldId(doc, pos + 1) ?? normalizeFieldId(node.attrs?.fieldId) ?? "_unscoped";
}

export interface BlockClarificationTriggerPayload {
  blockId: string;
  blockPos: number;
  fieldId: string;
  previewText: string;
  anchorRect: DOMRect;
  intent: "create" | "openExisting";
}

export type { BlockSelectionRange };

export interface ClarificationReference {
  fieldId?: string | null;
  blockId?: string | null;
  highlightedText?: string | null;
  tabKey?: string | null;
  status: "open" | "closed";
}

export interface BlockClarificationOptions {
  canCreate: () => boolean;
  getOpenCount: (fieldId: string, blockId: string) => number;
  getTotalCount: (fieldId: string, blockId: string) => number;
  activeTabKey?: string | null;
  getClarificationReferences?: () => ClarificationReference[];
  getHighlightedBlockKey: () => string | null;
  getSelectionHighlights?: (fieldId: string, blockId: string) => BlockSelectionRange[];
  getSelectionHighlight: (fieldId: string, blockId: string) => BlockSelectionRange | null;
  setHoveredBlockKey: (blockKey: string | null) => void;
  onTrigger: (payload: BlockClarificationTriggerPayload) => void;
}

const defaultOptions: BlockClarificationOptions = {
  canCreate: () => false,
  getOpenCount: () => 0,
  getTotalCount: () => 0,
  activeTabKey: null,
  getClarificationReferences: () => [],
  getHighlightedBlockKey: () => null,
  getSelectionHighlights: () => [],
  getSelectionHighlight: () => null,
  setHoveredBlockKey: () => {},
  onTrigger: () => {},
};

export const blockClarificationPluginKey = new PluginKey("blockClarification");
const BLOCK_CLARIFICATION_REFRESH_META = "blockClarificationRefresh";

type BlockAnchor = {
  fieldId: string;
  fieldLookupKey: string;
  blockId: string;
  blockKey: string;
  previewMatchText: string;
  fullMatchText: string;
};

type BlockCounts = {
  openCount: number;
  totalCount: number;
};

function toFieldLookupKey(fieldId: string): string {
  return fieldId.replace(/[^a-z0-9]/gi, "").toLowerCase();
}

function normalizeTabKey(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function matchesSnippet(anchor: BlockAnchor, snippet: string): boolean {
  if (!snippet) return false;
  return (
    anchor.fullMatchText.includes(snippet) ||
    snippet.includes(anchor.fullMatchText) ||
    anchor.previewMatchText.includes(snippet) ||
    snippet.includes(anchor.previewMatchText)
  );
}

function collectBlockAnchors(doc: Parameters<typeof DecorationSet.create>[0]): BlockAnchor[] {
  const anchors: BlockAnchor[] = [];

  doc.descendants((node, pos) => {
    if (!isClarificationBlockType(node.type.name)) {
      return;
    }

    const blockId = normalizeBlockId(node.attrs?.[BLOCK_ID_ATTR]) ?? `tmp_${pos}`;
    const fieldId = resolveBlockFieldId(doc, node, pos);
    const previewText = extractNodePreviewText(node);
    const fullText = compactMatchText(node.textContent ?? "");

    anchors.push({
      fieldId,
      fieldLookupKey: toFieldLookupKey(fieldId),
      blockId,
      blockKey: `${fieldId}:${blockId}`,
      previewMatchText: compactMatchText(previewText),
      fullMatchText: fullText,
    });

    // Treat supported block nodes as top-level clarification anchors.
    // Avoid descending into nested children (e.g., list item paragraphs)
    // to prevent stacked/duplicate action buttons for one logical block.
    return false;
  });

  return anchors;
}

function resolveReferenceTargetBlockKeys(
  anchors: BlockAnchor[],
  references: ClarificationReference[],
  activeTabKey?: string | null
): Array<string | null> {
  const anchorByExactKey = new Map<string, BlockAnchor>();
  const anchorsByBlockId = new Map<string, BlockAnchor[]>();
  const anchorsByField = new Map<string, BlockAnchor[]>();
  const anchorsByFieldLookup = new Map<string, BlockAnchor[]>();

  for (const anchor of anchors) {
    anchorByExactKey.set(anchor.blockKey, anchor);
    const blockAnchors = anchorsByBlockId.get(anchor.blockId);
    if (blockAnchors) blockAnchors.push(anchor);
    else anchorsByBlockId.set(anchor.blockId, [anchor]);
    const fieldAnchors = anchorsByField.get(anchor.fieldId);
    if (fieldAnchors) {
      fieldAnchors.push(anchor);
    } else {
      anchorsByField.set(anchor.fieldId, [anchor]);
    }
    const fieldLookupAnchors = anchorsByFieldLookup.get(anchor.fieldLookupKey);
    if (fieldLookupAnchors) {
      fieldLookupAnchors.push(anchor);
    } else {
      anchorsByFieldLookup.set(anchor.fieldLookupKey, [anchor]);
    }
  }

  const findSnippetAnchor = (
    fieldId: string,
    fieldLookupKey: string,
    snippet: string,
    allowGlobalFallback = true
  ): BlockAnchor | null => {
    if (!snippet) return null;

    if (fieldId !== "_unscoped" || fieldLookupKey !== "_unscoped") {
      const fieldAnchors = [
        ...(anchorsByField.get(fieldId) ?? []),
        ...(anchorsByFieldLookup.get(fieldLookupKey) ?? []),
      ];
      const fieldMatch = fieldAnchors.find((anchor) => matchesSnippet(anchor, snippet));
      if (fieldMatch) return fieldMatch;
    }

    if (!allowGlobalFallback) return null;
    return anchors.find((anchor) => matchesSnippet(anchor, snippet)) ?? null;
  };

  const findFallbackAnchor = (fieldId: string, fieldLookupKey: string): BlockAnchor | null => {
    if (fieldId !== "_unscoped") {
      const fieldAnchors = anchorsByField.get(fieldId) ?? [];
      if (fieldAnchors.length > 0) return fieldAnchors[0] ?? null;
    }
    if (fieldLookupKey !== "_unscoped") {
      const fieldLookupAnchors = anchorsByFieldLookup.get(fieldLookupKey) ?? [];
      if (fieldLookupAnchors.length > 0) return fieldLookupAnchors[0] ?? null;
    }
    return anchors[0] ?? null;
  };

  const normalizedActiveTabKey = normalizeTabKey(activeTabKey);
  const targetBlockKeys: Array<string | null> = [];

  for (const reference of references) {
    const referenceTabKey = normalizeTabKey(reference.tabKey);
    if (normalizedActiveTabKey) {
      if (!referenceTabKey || referenceTabKey !== normalizedActiveTabKey) {
        targetBlockKeys.push(null);
        continue;
      }
    } else if (referenceTabKey) {
      targetBlockKeys.push(null);
      continue;
    }

    const fieldId = normalizeFieldId(reference.fieldId) ?? "_unscoped";
    const fieldLookupKey = toFieldLookupKey(fieldId || "_unscoped");
    const blockId = normalizeBlockId(reference.blockId);
    const snippet = compactMatchText(reference.highlightedText ?? "");
    let targetAnchor: BlockAnchor | null = null;

    if (blockId) {
      const byExact = anchorByExactKey.get(`${fieldId}:${blockId}`) ?? null;
      const byBlock = anchorsByBlockId.get(blockId) ?? [];
      const byField =
        byBlock.find((anchor) => anchor.fieldId === fieldId) ??
        byBlock.find((anchor) => anchor.fieldLookupKey === fieldLookupKey) ??
        byBlock[0] ??
        null;
      targetAnchor = byExact ?? byField;
    }

    if (!targetAnchor && snippet.length > 0) {
      targetAnchor = findSnippetAnchor(fieldId, fieldLookupKey, snippet, true);
    }

    if (!targetAnchor) targetAnchor = findFallbackAnchor(fieldId, fieldLookupKey);
    targetBlockKeys.push(targetAnchor?.blockKey ?? null);
  }

  return targetBlockKeys;
}

export function resolveClarificationReferenceBlockKeys(
  doc: Parameters<typeof DecorationSet.create>[0],
  references: ClarificationReference[],
  activeTabKey?: string | null
): Array<string | null> {
  const anchors = collectBlockAnchors(doc);
  if (anchors.length === 0 || references.length === 0) {
    return references.map(() => null);
  }

  return resolveReferenceTargetBlockKeys(anchors, references, activeTabKey);
}

function buildCountsFromReferences(
  anchors: BlockAnchor[],
  references: ClarificationReference[],
  activeTabKey?: string | null
): Map<string, BlockCounts> {
  const countsByKey = new Map<string, BlockCounts>();
  for (const anchor of anchors) {
    countsByKey.set(anchor.blockKey, { openCount: 0, totalCount: 0 });
  }

  const targetBlockKeys = resolveReferenceTargetBlockKeys(anchors, references, activeTabKey);
  for (const [index, blockKey] of targetBlockKeys.entries()) {
    if (!blockKey) continue;
    const reference = references[index];
    if (!reference) continue;
    const current = countsByKey.get(blockKey) ?? { openCount: 0, totalCount: 0 };
    countsByKey.set(blockKey, {
      openCount: reference.status === "open" ? current.openCount + 1 : current.openCount,
      totalCount: current.totalCount + 1,
    });
  }

  return countsByKey;
}

function createActionWidget(
  fieldId: string,
  blockId: string,
  blockPos: number,
  previewText: string,
  openCount: number,
  forceVisible: boolean,
  onTrigger: (payload: BlockClarificationTriggerPayload) => void
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className =
    "df-block-clarification-widget pointer-events-none absolute -right-[2.75rem] top-1/2 z-20 -translate-y-1/2 group/clarification-action";

  const button = document.createElement("button");
  button.type = "button";
  const revealClass =
    openCount > 0
      ? "opacity-100 pointer-events-auto"
      : forceVisible
        ? "opacity-0 pointer-events-none touch:opacity-100 touch:pointer-events-auto"
        : "no-touch:opacity-0 no-touch:pointer-events-none no-touch:group-hover/clarification-block:opacity-100 no-touch:group-hover/clarification-block:pointer-events-auto no-touch:group-focus-within/clarification-block:opacity-100 no-touch:group-focus-within/clarification-block:pointer-events-auto touch:opacity-100 touch:pointer-events-auto";
  const alignmentClass = openCount > 0 ? "" : "translate-x-px";
  button.className = `df-block-clarification-trigger pointer-events-auto inline-flex h-8 min-w-8 items-center justify-center gap-1 rounded-md bg-background px-2 text-xs text-muted-foreground transition-colors hover:text-primary ${alignmentClass} ${revealClass}`;
  button.dataset.fieldId = fieldId;
  button.dataset.blockId = blockId;
  button.setAttribute("data-testid", "block-clarification-trigger");
  const tooltipText =
    openCount > 0
      ? `${openCount} open clarification request${openCount === 1 ? "" : "s"}`
      : "Request clarification";
  button.setAttribute("aria-label", tooltipText);
  button.title = tooltipText;

  if (openCount > 0) {
    const icon = createIconElement("messageSquare");
    icon.classList.add("shrink-0");
    const iconSlot = document.createElement("span");
    iconSlot.className = "inline-flex w-[14px] items-center justify-center";
    iconSlot.append(icon);
    const count = document.createElement("span");
    count.className = "font-semibold";
    count.textContent = String(openCount);
    button.append(iconSlot, count);
  } else {
    const icon = createIconElement("messageSquarePlus");
    icon.classList.add("shrink-0");
    const iconSlot = document.createElement("span");
    iconSlot.className = "inline-flex w-[14px] items-center justify-center";
    iconSlot.append(icon);
    const placeholder = document.createElement("span");
    placeholder.className = "font-semibold opacity-0 select-none";
    placeholder.textContent = "0";
    placeholder.setAttribute("aria-hidden", "true");
    button.append(iconSlot, placeholder);
  }

  const tooltip = document.createElement("span");
  const tooltipBaseClass =
    "pointer-events-none absolute z-30 w-max max-w-56 whitespace-normal break-words rounded bg-foreground px-2 py-1 text-left text-xs leading-4 text-background opacity-0 transition-opacity no-touch:group-hover/clarification-action:opacity-100 no-touch:group-focus-within/clarification-action:opacity-100";
  const tooltipRightClass = "left-full ml-1 top-1/2 -translate-y-1/2";
  const tooltipAboveClass = "right-0 bottom-full mb-1";
  const tooltipBelowClass = "right-0 top-full mt-1";
  tooltip.className = `${tooltipBaseClass} ${tooltipRightClass}`;
  tooltip.textContent = tooltipText;
  tooltip.setAttribute("role", "tooltip");

  const setTooltipPlacement = () => {
    const buttonRect = button.getBoundingClientRect();
    const tooltipWidth = tooltip.offsetWidth || 160;
    const tooltipHeight = tooltip.offsetHeight || 28;
    const viewportPadding = 8;
    const horizontalGap = 6;
    const verticalGap = 6;

    const hasRightSpace =
      window.innerWidth - buttonRect.right >= tooltipWidth + horizontalGap + viewportPadding;
    const hasTopSpace = buttonRect.top >= tooltipHeight + verticalGap + viewportPadding;
    const hasBottomSpace =
      window.innerHeight - buttonRect.bottom >= tooltipHeight + verticalGap + viewportPadding;

    if (hasRightSpace) {
      tooltip.className = `${tooltipBaseClass} ${tooltipRightClass}`;
      return;
    }
    if (hasTopSpace) {
      tooltip.className = `${tooltipBaseClass} ${tooltipAboveClass}`;
      return;
    }
    if (hasBottomSpace) {
      tooltip.className = `${tooltipBaseClass} ${tooltipBelowClass}`;
      return;
    }
    tooltip.className = `${tooltipBaseClass} ${tooltipAboveClass}`;
  };

  const fireTrigger = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    onTrigger({
      fieldId,
      blockId,
      blockPos,
      previewText,
      anchorRect: button.getBoundingClientRect(),
      intent: openCount > 0 ? "openExisting" : "create",
    });
  };

  button.addEventListener("mousedown", (event) => {
    event.preventDefault();
  });
  button.addEventListener("click", fireTrigger);
  button.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      fireTrigger(event);
    }
  });
  button.addEventListener("mouseenter", () => {
    setTooltipPlacement();
  });
  button.addEventListener("focus", () => {
    setTooltipPlacement();
  });

  wrapper.append(button, tooltip);

  if (forceVisible) {
    // Leaf nodes (images and atomic fields) don't provide a reliable inline
    // anchor context for widget placement, so we align by rendered block rect.
    let hoverBlockElement: HTMLElement | null = null;
    let observedBlockElement: HTMLElement | null = null;
    let blockResizeObserver: ResizeObserver | null = null;

    const alignToBlockMidpoint = (): HTMLElement | null => {
      const offsetParent = wrapper.offsetParent;
      if (!(offsetParent instanceof HTMLElement)) return null;

      const editorRoot = offsetParent.closest(".ProseMirror");
      if (!(editorRoot instanceof HTMLElement)) return null;

      const escapedFieldId = escapeSelectorValue(fieldId);
      const escapedBlockId = escapeSelectorValue(blockId);
      const exactSelector = `[data-df-field-id="${escapedFieldId}"][data-df-block-id="${escapedBlockId}"]`;
      const fallbackSelector = `[data-df-block-id="${escapedBlockId}"]`;
      const blockElement =
        editorRoot.querySelector<HTMLElement>(exactSelector) ??
        editorRoot.querySelector<HTMLElement>(fallbackSelector);
      if (!blockElement) return null;

      const offsetParentRect = offsetParent.getBoundingClientRect();
      const blockRect = blockElement.getBoundingClientRect();
      const midpoint = blockRect.top - offsetParentRect.top + blockRect.height / 2;
      const left = blockRect.right - offsetParentRect.left + 8;
      wrapper.style.left = `${left}px`;
      wrapper.style.right = "auto";
      wrapper.style.top = `${midpoint}px`;
      return blockElement;
    };

    const observeBlockSize = (blockElement: HTMLElement) => {
      if (typeof ResizeObserver === "undefined") return;
      if (!blockResizeObserver) {
        blockResizeObserver = new ResizeObserver(() => {
          alignToBlockMidpoint();
        });
      }
      if (observedBlockElement && observedBlockElement !== blockElement) {
        blockResizeObserver.unobserve(observedBlockElement);
      }
      if (observedBlockElement !== blockElement) {
        observedBlockElement = blockElement;
        blockResizeObserver.observe(blockElement);
      }

      const imageElement = blockElement.querySelector("img");
      if (imageElement && !imageElement.complete) {
        imageElement.addEventListener(
          "load",
          () => {
            alignToBlockMidpoint();
          },
          { once: true }
        );
      }
    };

    const refreshAnchor = () => {
      const blockElement = alignToBlockMidpoint();
      if (!blockElement) return;
      bindHoverTarget(blockElement);
      observeBlockSize(blockElement);
    };

    const showButton = () => {
      if (openCount > 0) return;
      refreshAnchor();
      button.classList.add("opacity-100", "pointer-events-auto");
      button.classList.remove("opacity-0", "pointer-events-none");
    };

    const hideButton = () => {
      if (openCount > 0) return;
      button.classList.add("opacity-0", "pointer-events-none");
      button.classList.remove("opacity-100", "pointer-events-auto");
    };

    const syncHoverVisibility = () => {
      if (openCount > 0) return;
      const blockHovered = hoverBlockElement?.matches(":hover") ?? false;
      const widgetHovered = wrapper.matches(":hover");
      if (blockHovered || widgetHovered) {
        showButton();
        return;
      }
      hideButton();
    };

    const handleBlockMouseEnter = () => {
      refreshAnchor();
      showButton();
    };
    const handleBlockMouseLeave = () => {
      requestAnimationFrame(() => {
        syncHoverVisibility();
      });
    };

    const bindHoverTarget = (blockElement: HTMLElement) => {
      if (hoverBlockElement === blockElement) return;

      if (hoverBlockElement) {
        hoverBlockElement.removeEventListener("mouseenter", handleBlockMouseEnter);
        hoverBlockElement.removeEventListener("mouseleave", handleBlockMouseLeave);
      }

      hoverBlockElement = blockElement;
      hoverBlockElement.addEventListener("mouseenter", handleBlockMouseEnter);
      hoverBlockElement.addEventListener("mouseleave", handleBlockMouseLeave);
    };

    wrapper.addEventListener("mouseleave", () => {
      requestAnimationFrame(() => {
        syncHoverVisibility();
      });
    });
    button.addEventListener("focus", showButton);
    button.addEventListener("blur", () => {
      requestAnimationFrame(() => {
        syncHoverVisibility();
      });
    });

    // Realign across a few frames to handle async image load/layout changes.
    const scheduleAlignment = (attempt = 0) => {
      refreshAnchor();
      syncHoverVisibility();
      if (attempt < 6) {
        requestAnimationFrame(() => {
          scheduleAlignment(attempt + 1);
        });
      }
    };

    requestAnimationFrame(() => {
      scheduleAlignment();
    });
  }

  return wrapper;
}

function buildDecorations(
  doc: Parameters<typeof DecorationSet.create>[0],
  options: BlockClarificationOptions
): DecorationSet {
  const decorations: Decoration[] = [];
  const blockAnchors = collectBlockAnchors(doc);
  const references = options.getClarificationReferences?.() ?? [];
  const referenceCounts =
    references.length > 0
      ? buildCountsFromReferences(blockAnchors, references, options.activeTabKey)
      : null;

  doc.descendants((node, pos) => {
    if (!isClarificationBlockType(node.type.name)) {
      return;
    }

    const blockId = normalizeBlockId(node.attrs?.[BLOCK_ID_ATTR]) ?? `tmp_${pos}`;

    const fieldId = resolveBlockFieldId(doc, node, pos);

    const blockCounts = referenceCounts?.get(`${fieldId}:${blockId}`);
    const directOpenCount = Math.max(0, options.getOpenCount(fieldId, blockId));
    const referenceOpenCount = Math.max(0, blockCounts?.openCount ?? 0);
    const openCount = Math.max(directOpenCount, referenceOpenCount);
    const blockKey = `${fieldId}:${blockId}`;
    const isHighlighted = options.getHighlightedBlockKey() === blockKey;
    const isListBlock = isListNode(node.type.name);
    const highlightClassName =
      isHighlighted && isListBlock
        ? "df-clarification-block-list-highlight"
        : isHighlighted
          ? "-mx-1 rounded-sm bg-primary/10 px-1 ring-1 ring-primary/40"
          : "";
    const className = [
      "df-clarification-block",
      "group/clarification-block",
      "relative",
      "after:absolute after:-right-12 after:inset-y-0 after:w-12 after:content-['']",
      "transition-colors",
      isMarkupNode(node.type.name) ? "df-clarification-block-markup" : "",
      highlightClassName,
    ]
      .filter(Boolean)
      .join(" ");

    decorations.push(
      Decoration.node(
        pos,
        pos + node.nodeSize,
        {
          class: className,
          "data-df-block-id": blockId,
          "data-df-field-id": fieldId,
        },
        { key: `df-block-node:${blockKey}` }
      )
    );

    const highlightRanges = options.getSelectionHighlights
      ? options.getSelectionHighlights(fieldId, blockId)
      : [];
    const fallbackHighlightRange = options.getSelectionHighlight(fieldId, blockId);
    if (fallbackHighlightRange) {
      highlightRanges.push(fallbackHighlightRange);
    }

    for (const highlightRange of highlightRanges) {
      if (!highlightRange || highlightRange.to <= highlightRange.from) continue;
      const from = Math.max(pos + 1, highlightRange.from);
      const to = Math.min(pos + node.nodeSize - 1, highlightRange.to);
      if (to <= from) continue;

      decorations.push(
        Decoration.inline(
          from,
          to,
          {
            class: CLARIFICATION_SELECTION_TEXT_CLASS,
          },
          { key: `df-block-selection:${blockKey}:${from}:${to}` }
        )
      );

      for (const atomRange of findInlineAtomRangesInRange(doc, { from, to })) {
        decorations.push(
          Decoration.node(
            atomRange.from,
            atomRange.to,
            {
              class: CLARIFICATION_SELECTION_ATOM_CLASS,
            },
            { key: `df-block-selection-atom:${blockKey}:${atomRange.from}:${atomRange.to}` }
          )
        );
      }
    }

    const previewText = extractNodePreviewText(node);
    const usesBlockRectAnchor = node.isLeaf || isAtomicNode(node.type.name);
    // The affordance is absolutely positioned against the block, so where inside the block's
    // inline content it is anchored has no effect on where it is drawn — but it is not inert.
    // A widget anchored at the *end* of a textblock is rendered between the block's last
    // inline node and the `img.ProseMirror-separator` ProseMirror appends so that the caret
    // position after a trailing `contenteditable="false"` atom stays paintable (LAT-926).
    // With the widget wedged in between, Chrome no longer resolves that caret position to the
    // separator and re-homes typed text into the last real text node instead — text typed
    // after a paragraph-ending citation landed *before* it. Anchor the affordance at the start
    // of the inline content, where no such hack node depends on adjacency.
    const anchorPos = usesBlockRectAnchor ? pos + node.nodeSize : pos + 1;

    const isMarkup = isMarkupNode(node.type.name);
    const canCreateForBlock =
      options.canCreate() &&
      (isMarkup || isAtomicNode(node.type.name) || hasMeaningfulBlockContent(node));
    const shouldRenderAction = canCreateForBlock || openCount > 0;
    if (shouldRenderAction) {
      const actionDecorationKey = `df-block-action:${blockKey}:${openCount}`;
      decorations.push(
        Decoration.widget(
          anchorPos,
          () =>
            createActionWidget(
              fieldId,
              blockId,
              pos,
              previewText,
              openCount,
              usesBlockRectAnchor,
              options.onTrigger
            ),
          {
            // Before any content at the anchor, so a caret at the start of the block sits
            // after the widget rather than being pushed in front of it.
            side: usesBlockRectAnchor ? 0 : -1,
            key: actionDecorationKey,
          }
        )
      );
    }

    return false;
  });

  return DecorationSet.create(doc, decorations);
}

export const BlockClarification = Extension.create<BlockClarificationOptions>({
  name: "blockClarification",

  addOptions() {
    return defaultOptions;
  },

  addProseMirrorPlugins() {
    const options = this.options;

    return [
      new Plugin({
        key: blockClarificationPluginKey,
        state: {
          init: (_config, state) => buildDecorations(state.doc, options),
          apply: (tr, oldDecorations, _oldState, newState) => {
            const shouldRefresh = Boolean(
              tr.getMeta(blockClarificationPluginKey)?.refresh ||
                tr.getMeta(BLOCK_CLARIFICATION_REFRESH_META)
            );
            if (tr.docChanged || tr.selectionSet || shouldRefresh) {
              return buildDecorations(newState.doc, options);
            }
            return oldDecorations.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state) as DecorationSet;
          },
        },
      }),
    ];
  },
});

export const __testOnly = {
  buildDecorations,
  buildCountsFromReferences,
  collectBlockAnchors,
  resolveClarificationReferenceBlockKeys,
};
